import type { Resolution } from './resolution.type.ts';

/**
 * Read-only projection of a `Resolution` record scoped to the loan-enum
 * domains (`LoanType` and `RepaymentPlan`).
 *
 * Powers the admin "Loan Resolution Review Queue" — the cross-person
 * counterpart to the per-loan badges. Backed by
 * `GET /api/v1/resolutions` (Api::V1::ResolutionResource).
 *
 * Note: `resolvable` is polymorphic on the server (Enum rows for LoanType
 * / RepaymentPlan). We intentionally do NOT model it as a relationship
 * here — the queue only needs the type/id + label/name quartet to
 * display, not to hydrate. The server exposes `resolvableLabel` (friendly
 * description) and `resolvableName` (machine key) directly so consumers
 * don't have to dereference a polymorphic relationship.
 */
// Nullable attrs below mirror the server contract: a freshly-created
// `Resolution` may have no source assigned (background classifier hasn't
// run yet) and no admin action (queue entry awaiting review). The
// `resolvable*` quartet is null for unresolved rows where the matcher
// produced no candidate. Modelling these as nullable surfaces "no value"
// in the template instead of forcing a fake placeholder.
export interface ResolutionExtension extends Resolution {}
export class ResolutionExtension {
  get hasPendingReview() {
    // == null catches both an explicit null from the API and an omitted
    // attr (which Ember Data surfaces as undefined).
    return this.adminAction == null && this.resolutionSource === 'opensearch';
  }

  get isAdminConfirmed() {
    return this.adminAction === 'confirmed';
  }

  get isAdminRejected() {
    return this.adminAction === 'rejected';
  }

  /** Sampled owners, deduped server-side; empty (never undefined) for rows that predate provenance tracking. */
  get provenancePeople() {
    return this.people ?? [];
  }

  /** Sampled uploads, newest first; empty (never undefined) for rows that predate provenance tracking. */
  get provenanceSourceDocuments() {
    return this.sourceDocuments ?? [];
  }

  /** True when the sample carries at least one owner or upload — the populated vs "no source recorded" switch. */
  get hasProvenance() {
    return this.provenancePeople.length > 0 || this.provenanceSourceDocuments.length > 0;
  }

  /** How many owners the served sample leaves out — the server caps the list, not the count. */
  get hiddenPeopleCount() {
    return Math.max(0, (this.peopleCount ?? 0) - this.provenancePeople.length);
  }

  /** How many uploads the served sample leaves out; clamped to 0 when the total trails the sample. */
  get hiddenSourceDocumentsCount() {
    return Math.max(0, (this.sourceDocumentsCount ?? 0) - this.provenanceSourceDocuments.length);
  }
}

const Registration = {
  kind: 'object',
  name: 'resolution',
  features: ResolutionExtension,
};
export default Registration;
